// src/constants/trigger-types.js

/**
 * Dictionary of all trigger types with their display text
 */
export const triggerTypesDictionary = {
    // Experience-level triggers
    trigger_experience_starts: 'Immediately',
    trigger_tap_to_place: 'User pins experience',
    trigger_match_to_place: 'Match to Place',
    trigger_screen_tap: 'User taps screen',
    trigger_image_anchor_found: 'User finds reference image',
    trigger_image_anchor_lost: 'Image Lost',
    trigger_user_aligned_anchor: 'User aligned stencil',
    trigger_event_received: 'Event Received',
    trigger_mqtt_message_received: 'MQTT Message Received',
    trigger_amqp_message_received: 'AMQP Message Received',

    // Object-level triggers
    trigger_tap: 'User taps object',
    trigger_proximity_enter: 'User moves close to object',
    trigger_proximity_exit: 'User moves away from object',
    trigger_collision_enter: 'Object starts colliding with another',
    trigger_collision_exit: 'Object ends colliding with another',
    trigger_focus_in: 'User points phone at object',
    trigger_focus_out: 'User points phone away from object',
    trigger_enter_portal: 'User enters 360 portal',
    trigger_exit_portal: 'User exits 360 portal',
};

/**
 * Dictionary of experience-level triggers (not tied to specific objects)
 */
export const experienceTriggerTypesDictionary = {
    trigger_experience_starts: 'Immediately',
    trigger_tap_to_place: 'User pins experience',
    trigger_match_to_place: 'Match to Place',
    trigger_screen_tap: 'User taps screen',
    trigger_image_anchor_found: 'User finds reference image',
    trigger_image_anchor_lost: 'Image Lost',
    trigger_user_aligned_anchor: 'User aligned stencil',
    trigger_event_received: 'Event Received',
    trigger_mqtt_message_received: 'MQTT Message Received',
    trigger_amqp_message_received: 'AMQP Message Received',
};

/**
 * Dictionary of object-level triggers (tied to specific objects)
 */
export const objectTriggerTypesDictionary = {
    trigger_tap: 'User taps object',
    trigger_proximity_enter: 'User moves close to object',
    trigger_proximity_exit: 'User moves away from object',
    trigger_collision_enter: 'Object collides with another object',
    trigger_collision_exit: 'Object ends collision with another object',
    trigger_focus_in: 'User points phone at object',
    trigger_focus_out: 'User points phone away from object',
    trigger_enter_portal: 'User enters 360 portal',
    trigger_exit_portal: 'User exits 360 portal',
};

/**
 * Trigger types that should repeat by default
 */
export const repeatingTriggerTypes = [
    'trigger_tap',
    'trigger_proximity_enter',
    'trigger_proximity_exit',
    'trigger_collision_enter',
    'trigger_collision_exit',
    'trigger_focus_in',
    'trigger_focus_out',
    'trigger_enter_portal',
    'trigger_exit_portal',
];

/**
 * Trigger types that require a reference image
 */
export const referenceImageTriggerTypes = [
    'trigger_image_anchor_found',
    'trigger_image_anchor_lost',
];

/**
 * Trigger types that require a stencil
 */
export const stencilTriggerTypes = [
    'trigger_user_aligned_anchor',
];

/**
 * Trigger types that need target objects
 */
export const objectTargetingTriggerTypes = [
    'trigger_tap',
    'trigger_proximity_enter',
    'trigger_proximity_exit',
    'trigger_collision_enter',
    'trigger_collision_exit',
    'trigger_focus_in',
    'trigger_focus_out',
    'trigger_enter_portal',
    'trigger_exit_portal',
];

/**
 * Trigger types that support instructions
 */
export const instructionSupportingTriggerTypes = [
    'trigger_image_anchor_found',
    'trigger_screen_tap',
    'trigger_user_aligned_anchor',
    'trigger_match_to_place',
];

/**
 * Gets the display text for a trigger type
 * 
 * @param {string} triggerType - The trigger type value
 * @returns {string} The display text for the trigger type
 */
export const getTriggerTypeText = (triggerType) => {
    return triggerTypesDictionary[triggerType] || triggerType;
};

/**
 * Checks if a trigger type should repeat by default
 * 
 * @param {string} triggerType - The trigger type value
 * @returns {boolean} Whether the trigger type should repeat by default
 */
export const shouldTriggerRepeat = (triggerType) => {
    return repeatingTriggerTypes.includes(triggerType);
};

/**
 * Checks if a trigger type requires target objects
 * 
 * @param {string} triggerType - The trigger type value
 * @returns {boolean} Whether the trigger type requires target objects
 */
export const requiresTargetObjects = (triggerType) => {
    return objectTargetingTriggerTypes.includes(triggerType);
};

/**
 * Checks if a trigger type supports instructions
 * 
 * @param {string} triggerType - The trigger type value
 * @returns {boolean} Whether the trigger type supports instructions
 */
export const supportsInstructions = (triggerType) => {
    return instructionSupportingTriggerTypes.includes(triggerType);
};

/**
 * Gets all available trigger types based on user role
 * 
 * @param {string} userRole - The user's role ('admin', 'staff', or 'user')
 * @returns {Object} Dictionary of available trigger types
 */
export const getAvailableTriggerTypes = (userRole = 'user') => {
    // Start with all trigger types
    const available = { ...triggerTypesDictionary };

    // Filter based on user role
    if (userRole !== 'admin' && userRole !== 'staff') {
        // Remove admin/staff-only trigger types for regular users
        delete available.trigger_user_aligned_anchor;
        delete available.trigger_mqtt_message_received;
        delete available.trigger_amqp_message_received;
    }

    return available;
};